import { z } from 'zod';
import { uuid, isoTimestamp, storeCode } from './common.js';
import { SYNC_TABLES } from '../enums.js';
import { listItemRow, genericProductRow, productVariantRow, variantAttributeRow, suggestionDismissalRow, storeCategoryOrderRow, } from './rows.js';
/**
 * The sync protocol (API §3, DATABASE §12).
 *
 * Two things in here carry most of the design's weight:
 *
 * 1. Resolution is **last-write-wins per row**, not per field. There is one
 *    `updated_at` per row and no per-field timestamps, so per-field merging is
 *    not computable (REVIEW B1). An incoming row is applied whole or discarded.
 *
 * 2. Check-off is **its own mutation type and exempt from LWW**. It is not a
 *    row write. A stale outbox replay must never resurrect an item already
 *    bought, and idempotency comes from the partial unique index on
 *    purchases(list_item_id) rather than from timestamp comparison.
 */
export const syncTable = z.enum(SYNC_TABLES);
/** Idempotency key, generated once by the client and replayed from the outbox. */
const mutationId = z.object({ id: uuid });
/**
 * Each option is a complete flat object rather than an intersection, because
 * `z.discriminatedUnion` only accepts ZodObject members — an intersection
 * cannot expose the discriminant key for the fast dispatch it does.
 */
const upsertMutation = z.discriminatedUnion('table', [
    mutationId.extend({ op: z.literal('upsert'), table: z.literal('list_items'), row: listItemRow }),
    mutationId.extend({
        op: z.literal('upsert'),
        table: z.literal('generic_products'),
        row: genericProductRow,
    }),
    mutationId.extend({
        op: z.literal('upsert'),
        table: z.literal('product_variants'),
        row: productVariantRow,
    }),
    mutationId.extend({
        op: z.literal('upsert'),
        table: z.literal('variant_attributes'),
        row: variantAttributeRow,
    }),
    mutationId.extend({
        op: z.literal('upsert'),
        table: z.literal('suggestion_dismissals'),
        row: suggestionDismissalRow,
    }),
    mutationId.extend({
        op: z.literal('upsert'),
        table: z.literal('store_category_order'),
        row: storeCategoryOrderRow,
    }),
]);
/** A soft delete. `op: "delete"` sets `deleted_at`; rows are never hard-deleted. */
const deleteMutation = mutationId.extend({
    op: z.literal('delete'),
    table: syncTable,
    row: z.object({ id: uuid, updated_at: isoTimestamp }),
});
/**
 * Ticking an item means bought (ARCHITECTURE §6.6). `store` is required only
 * for "Any" items — it is where the store gets resolved, and what teaches
 * history which shop this household actually buys milk at.
 */
const checkoffMutation = mutationId.extend({
    op: z.literal('checkoff'),
    list_item_id: uuid,
    store: storeCode.nullish(),
    purchased_at: isoTimestamp.optional(),
});
export const mutation = z.union([upsertMutation, deleteMutation, checkoffMutation]);
export const pushRequest = z.object({
    mutations: z.array(mutation).max(500),
});
/**
 * Rejections are for **validation failures only**, never for conflicts —
 * conflicts resolve silently by last-write-wins (API §3.2).
 */
export const pushResponse = z.object({
    applied: z.array(uuid),
    rejected: z.array(z.object({ id: uuid, code: z.string(), message: z.string() })),
    server_time: isoTimestamp,
});
/**
 * REVIEW G3: an omitted `since` means a full sync, and `has_more` pairs with a
 * cursor the client passes back — neither was defined before.
 *
 * `since` always comes from the previous response's `server_time`, never from
 * the client clock: phone clocks drift and users change timezones, so the
 * server's clock is the only ordering authority (API §3.1).
 */
export const pullQuery = z.object({
    since: isoTimestamp.optional(),
    cursor: z.string().max(500).optional(),
    limit: z.coerce.number().int().min(1).max(2000).default(1000),
});
export const pullResponse = z.object({
    server_time: isoTimestamp,
    changes: z.object({
        generic_products: z.array(z.record(z.unknown())),
        product_variants: z.array(z.record(z.unknown())),
        variant_attributes: z.array(z.record(z.unknown())),
        list_items: z.array(z.record(z.unknown())),
        suggestion_dismissals: z.array(z.record(z.unknown())),
        store_category_order: z.array(z.record(z.unknown())),
    }),
    has_more: z.boolean(),
    /** Present only when has_more is true. Opaque; pass it back verbatim. */
    cursor: z.string().optional(),
    /**
     * The watermark this device sent was older than the tombstone retention
     * window (REVIEW G4), so `since` was ignored and this is a full sync.
     *
     * It matters because a full sync is not enough on its own. Tombstones older
     * than the window have been hard-deleted, so the deletions this device never
     * saw are not in the payload and never will be: merging leaves ghost items on
     * its list for good. The client has to treat the payload as the whole truth
     * and drop local rows that are not in it.
     */
    full_resync: z.boolean().optional(),
});
//# sourceMappingURL=sync.js.map